import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ColegioService from '../services/ColegioService';
import { useNavigate } from 'react-router-dom';
import DataTable from './common/DataTable';
import Alert from './common/Alert';
import ChatModal from './common/ChatModal';
import AsistenteService from '../services/AsistenteService';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import { hasOpenAIChatsEnabled, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';
import './MyAssistants.css';
import ProfessorMenu from './common/menus/ProfessorMenu';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const DEFAULT_ITEMS_PER_PAGE = 10;

const getRange = (page, pageSize) => {
  const currentPage = Math.max(page || 1, 1);
  const take = Math.max(pageSize || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = (currentPage - 1) * take;
  return { start, end: start + take - 1 };
};

const normalizeAssistant = (asistente) => {
  const fechaRaw =
    asistente.fechaCreacion ??
    asistente.FechaCreacion ??
    asistente.fechaAlta ??
    asistente.FechaAlta ??
    asistente.fechaAsignacion ??
    asistente.FechaAsignacion ??
    null;
  const fecha = fechaRaw ? new Date(fechaRaw) : null;

  return {
    ...asistente,
    nombreAsistente: asistente.nombreAsistente || asistente.NombreAsistente || asistente.nombre || asistente.Nombre,
    nombreArea: asistente.nombreArea || asistente.NombreArea,
    nombreMateria: asistente.nombreMateria || asistente.NombreMateria,
    passwordAsistente: asistente.passwordAsistente || asistente.PasswordAsistente,
    fechaAlta: fecha && !Number.isNaN(fecha.getTime()) ? fecha : null
  };
};

const MyAssistants = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const isProfesor = isProfessorUser(user);
  const isAdmin = normalizeRoleName(user).includes('admin');
  const navigate = useNavigate();
  
  // Estados
  const [asistentesPropios, setAsistentesPropios] = useState([]);
  const [asistentesMateriaOtros, setAsistentesMateriaOtros] = useState([]);
  const [isLoadingPropios, setIsLoadingPropios] = useState(true);
  const [isLoadingOtros, setIsLoadingOtros] = useState(true);
  const [paginacionPropios, setPaginacionPropios] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [paginacionOtros, setPaginacionOtros] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const hasModuloAlumnos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'ModuloAlumnos');
  }, []);
  const hasAgentesClasicos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');
  }, []);
  const hasOpenAIChats = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return hasOpenAIChatsEnabled(flags);
  }, []);

  const isLoading = isLoadingPropios || isLoadingOtros;

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } });
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (authLoading) return;

    if (isAdmin) {
      setIsLoadingPropios(false);
      setIsLoadingOtros(false);
      return;
    }

    if (!isProfesor) {
      setIsLoadingPropios(false);
      setIsLoadingOtros(false);
      return;
    }

    if (!hasOpenAIChats) {
      setAsistentesPropios([]);
      setAsistentesMateriaOtros([]);
      setPaginacionPropios((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setPaginacionOtros((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingPropios(false);
      setIsLoadingOtros(false);
      return;
    }
  }, [authLoading, isProfesor, isAdmin, hasOpenAIChats, navigate]);

  const loadAsistentesPropios = async (pagina = paginacionPropios.paginaActual, tamañoPagina = paginacionPropios.tamañoPagina) => {
    if (authLoading || isAdmin || !isProfesor || !hasOpenAIChats) return;

    try {
      setIsLoadingPropios(true);
      setAlert({ show: false, message: '', type: 'error', title: '' });
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await AsistenteService.getMisAsistentes({
        start,
        end,
        esClasico: false,
        esAsignado: true
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al cargar los asistentes');
      }

      setAsistentesPropios((response.asistentes || []).map(normalizeAssistant));
      setPaginacionPropios((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response.total ?? 0
      }));
    } catch (error) {
      console.error('Error al cargar asistentes propios:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes creados. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentesPropios([]);
      setPaginacionPropios((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingPropios(false);
    }
  };

  const loadAsistentesOtros = async (pagina = paginacionOtros.paginaActual, tamañoPagina = paginacionOtros.tamañoPagina) => {
    if (authLoading || isAdmin || !isProfesor || !hasOpenAIChats) return;

    try {
      setIsLoadingOtros(true);
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await AsistenteService.getMisAsistentes({
        start,
        end,
        esClasico: false,
        esAsignado: false
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al cargar los asistentes');
      }

      setAsistentesMateriaOtros((response.asistentes || []).map(normalizeAssistant));
      setPaginacionOtros((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response.total ?? 0
      }));
    } catch (error) {
      console.error('Error al cargar asistentes de otros miembros:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes de la materia.',
        type: 'error'
      });
      setAsistentesMateriaOtros([]);
      setPaginacionOtros((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingOtros(false);
    }
  };

  useEffect(() => {
    if (!isProfesor || isAdmin || authLoading || !hasOpenAIChats) return;
    loadAsistentesPropios(paginacionPropios.paginaActual, paginacionPropios.tamañoPagina);
  }, [isProfesor, isAdmin, authLoading, hasOpenAIChats, paginacionPropios.paginaActual, paginacionPropios.tamañoPagina]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isProfesor || isAdmin || authLoading || !hasOpenAIChats) return;
    loadAsistentesOtros(paginacionOtros.paginaActual, paginacionOtros.tamañoPagina);
  }, [isProfesor, isAdmin, authLoading, hasOpenAIChats, paginacionOtros.paginaActual, paginacionOtros.tamañoPagina]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    if (isAdmin || !isProfesor || !hasOpenAIChats) return;
    loadAsistentesPropios(paginacionPropios.paginaActual, paginacionPropios.tamañoPagina);
    loadAsistentesOtros(paginacionOtros.paginaActual, paginacionOtros.tamañoPagina);
  };

  const handleNewAssistant = () => {
    navigate('/new-assistant');
  };

  const handleLogout = () => {
    logout();
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleTestAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setSelectedAssistant(null);
  };

  const handleEditAssistant = (assistant) => {
    if (!assistant?.idOpenAIAssistant) return;
    navigate(`/assistants/${assistant.idOpenAIAssistant}/edit`);
  };

  const handleDeleteClick = (assistant) => {
    setAssistantToDelete(assistant);
  };

  const closeDeleteModal = () => {
    setAssistantToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!assistantToDelete?.idOpenAIAssistant) return;
    const assistantId = assistantToDelete.idOpenAIAssistant;
    setAssistantToDelete(null);
    try {
      setIsDeleting(true);
      setAlert({
        show: true,
        title: 'Eliminando asistente',
        message: 'Estamos eliminando el asistente seleccionado. Este proceso puede tardar unos segundos.',
        type: 'info'
      });

      await AsistenteService.eliminarAsistenteOpenAI(assistantId);

      setAlert({
        show: true,
        title: 'Asistente eliminado',
        message: 'El asistente se eliminó correctamente.',
        type: 'success'
      });
      const paginaDestino = asistentesPropios.length === 1 && paginacionPropios.paginaActual > 1
        ? paginacionPropios.paginaActual - 1
        : paginacionPropios.paginaActual;

      if (paginaDestino !== paginacionPropios.paginaActual) {
        setPaginacionPropios((prev) => ({ ...prev, paginaActual: paginaDestino }));
      } else {
        loadAsistentesPropios(paginaDestino, paginacionPropios.tamañoPagina);
      }
    } catch (error) {
      console.error('Error al eliminar asistente:', error);
      setAlert({
        show: true,
        title: 'Error al eliminar asistente',
        message: error.message || 'No se pudo eliminar el asistente. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Redirigiendo a Asistentes OpenAI..." />
        </div>
      </div>
    );
  }

  if (!isProfesor) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Esta sección está disponible únicamente para usuarios con rol Profesor.</p>
        </div>
      </div>
    );
  }

  if (!hasOpenAIChats) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Chats OpenAI deshabilitados</h2>
          <p>Este colegio no tiene habilitada la feature flag `PermiteChatsOpenAI`.</p>
        </div>
      </div>
    );
  }

  const handlePropiosPageChange = (pagina) => {
    setPaginacionPropios((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handlePropiosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionPropios((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handleOtrosPageChange = (pagina) => {
    setPaginacionOtros((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handleOtrosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionOtros((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    
    return fecha.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Configuración de columnas para la tabla (asistentes propios)
  const columnsPropios = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'areaMateria',
      title: 'Área/Materia',
      sortable: true,
      render: (value, item) => {
        const area = item.nombreArea || item.NombreArea || '—';
        const materia = item.nombreMateria || item.NombreMateria || '—';
        return `${area}/${materia}`;
      }
    },
    {
      key: 'passwordAsistente',
      title: 'Password',
      sortable: false,
      render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-edit"
            title="Editar asistente OpenAI"
            onClick={() => handleEditAssistant(item)}
          >
            <i className="fas fa-pen"></i>
          </button>
          <button
            className="table-btn table-btn-delete"
            title="Eliminar asistente"
            onClick={() => handleDeleteClick(item)}
          >
            <i className="fas fa-trash"></i>
          </button>
          <button
            className="table-btn table-btn-test"
            title="Probar asistente"
            onClick={() => handleTestAssistant(item)}
          >
            <i className="fas fa-play"></i>
          </button>
        </div>
      )
    }
  ];

  // Configuración de columnas para la tabla (asistentes de materias de otros)
  const columnsOtros = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'areaMateria',
      title: 'Área/Materia',
      sortable: true,
      render: (value, item) => {
        const area = item.nombreArea || item.NombreArea || '—';
        const materia = item.nombreMateria || item.NombreMateria || '—';
        return `${area}/${materia}`;
      }
    },
    {
      key: 'passwordAsistente',
      title: 'Password',
      sortable: false,
      render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-test"
            title="Probar asistente"
            onClick={() => handleTestAssistant(item)}
          >
            <i className="fas fa-play"></i>
          </button>
        </div>
      )
    }
  ];

  const handleNavigate = (item) => {
    if (!item?.path) return;
    setIsSidebarOpen(false);
    navigate(item.path);
  };

  return (
    <div className={`new-dashboard my-assistants ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        
        <ProfessorMenu
          activeId="mis-asistentes-openai"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={handleNavigate}
        />
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Mis Asistentes OpenAI
            </h1>
            <p className="page-subtitle">Gestiona todos tus asistentes OpenAI</p>
          </div>
          
          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            <UserSessionMenu user={user} onLogout={handleLogout} />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Assistants Summary */}
          <div className="assistants-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="summary-content">
                <h2 className="summary-number">{paginacionPropios.total}</h2>
                <p className="summary-text">
                  {paginacionPropios.total === 1 ? 'Asistente creado' : 'Asistentes creados'}
                </p>
                <div className="summary-description">
                  {paginacionPropios.total === 0 
                    ? 'Aún no has creado ningún asistente OpenAI'
                    : 'Listos para ayudar a los alumnos'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* CTA Nuevo asistente */}
          <div className="assistant-cta" style={{ marginBottom: '1.5rem' }}>
            <div className="cta-text">
              <h3>Crea un nuevo asistente OpenAI</h3>
              <p>Comparte material y habilítalo para tus alumnos en segundos.</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNewAssistant}
            >
              <i className="fas fa-plus"></i>
              Nuevo Asistente OpenAI
            </button>
          </div>

          {/* Table */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-list"></i>
                Lista de Asistentes OpenAI
              </h2>
            </div>
            
            <div className="card-content">
              <DataTable
                data={asistentesPropios}
                columns={columnsPropios}
                loading={isLoadingPropios}
                loadingMessage="Obteniendo mis asistentes..."
                emptyMessage="No tienes asistentes creados aún. ¡Crea tu primer asistente!"
                className="assistants-table"
                externalPagination={true}
                externalTotalItems={paginacionPropios.total}
                externalTotalPages={Math.max(1, Math.ceil(paginacionPropios.total / paginacionPropios.tamañoPagina))}
                externalCurrentPage={paginacionPropios.paginaActual}
                onPageChange={handlePropiosPageChange}
                onItemsPerPageChange={handlePropiosItemsPerPageChange}
                defaultItemsPerPage={paginacionPropios.tamañoPagina}
              />
            </div>
          </div>

          <div className="content-card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-people-group"></i>
                Asistentes de la materia creados por otros miembros de la institución
              </h2>
            </div>
            <div className="card-content">
              <DataTable
                data={asistentesMateriaOtros}
                columns={columnsOtros}
                loading={isLoadingOtros}
                loadingMessage="Obteniendo asistentes de materias..."
                emptyMessage="No hay asistentes de materias creados por otros miembros."
                className="assistants-table"
                externalPagination={true}
                externalTotalItems={paginacionOtros.total}
                externalTotalPages={Math.max(1, Math.ceil(paginacionOtros.total / paginacionOtros.tamañoPagina))}
                externalCurrentPage={paginacionOtros.paginaActual}
                onPageChange={handleOtrosPageChange}
                onItemsPerPageChange={handleOtrosItemsPerPageChange}
                defaultItemsPerPage={paginacionOtros.tamañoPagina}
              />
            </div>
          </div>
        </div>
      </main>

      {assistantToDelete && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>¿Eliminar asistente?</h3>
            <p>A continuación, aceptando, usted eliminará el asistente junto con sus archivos, esta acción no se podrá deshacer. ¿Está usted seguro?</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button className="btn btn-danger" type="button" onClick={handleConfirmDelete}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChatModal}
        assistant={selectedAssistant}
      />
      {isDeleting && (
        <LoadingSpinner message="Eliminando asistente..." />
      )}
    </div>
  );
};

export default MyAssistants;
